import OpenAI, { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { supabase } from '../lib/supabase';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const openaiComplianceService = {
  /**
   * Analyze payroll data for compliance violations using OpenAI
   */
  async analyzePayrollCompliance(payrollData, options = {}) {
    try {
      const prompt = `You are a PNG Employment Act compliance expert. Analyze the following payroll data and identify any compliance violations:

Payroll Data:
${JSON.stringify(payrollData, null, 2)}

Focus on:
- Minimum wage compliance (PNG K4.50/hour)
- Overtime calculations (1.5x for overtime, 2x for public holidays)
- NASFUND contributions (8.4% employer, 6% employee)
- SWT (Salary and Wages Tax) compliance
- Leave entitlements
- Public holiday pay

Provide a detailed analysis with specific violations found.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { role: 'system', content: 'You are an expert in PNG Employment Act compliance and payroll regulations.' },
          { role: 'user', content: prompt },
        ],
        reasoning_effort: 'high',
        verbosity: 'high',
        max_completion_tokens: 2000
      });

      return {
        analysis: response?.choices?.[0]?.message?.content,
        timestamp: new Date()?.toISOString()
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error analyzing payroll compliance:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Generate AI-powered remediation steps for compliance violations
   */
  async generateRemediationSteps(violations) {
    try {
      const violationsList = violations?.map(v => 
        `- ${v?.violationType}: ${v?.description} (Severity: ${v?.severity}, Affected: ${v?.affectedCount} employees)`
      )?.join('\n');

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5',
        messages: [
          { 
            role: 'system', 
            content: 'You are a PNG Employment Act compliance expert. Generate detailed, actionable remediation steps for compliance violations. Be specific and practical.' 
          },
          { 
            role: 'user', 
            content: `Generate detailed remediation steps for these compliance violations:\n\n${violationsList}\n\nFor each violation, provide:\n1. Immediate actions required\n2. Step-by-step correction process\n3. Timeline for implementation\n4. Preventive measures\n5. Documentation requirements` 
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'remediation_response',
            schema: {
              type: 'object',
              properties: {
                remediations: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      violationType: { type: 'string' },
                      immediateActions: { type: 'array', items: { type: 'string' } },
                      correctionSteps: { type: 'array', items: { type: 'string' } },
                      timeline: { type: 'string' },
                      preventiveMeasures: { type: 'array', items: { type: 'string' } },
                      documentation: { type: 'array', items: { type: 'string' } }
                    },
                    required: ['violationType', 'immediateActions', 'correctionSteps', 'timeline']
                  }
                }
              },
              required: ['remediations'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',
        verbosity: 'high'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating remediation steps:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Scan payroll data for violations before submission deadline
   */
  async scanPayrollBeforeDeadline(filters = {}) {
    try {
      // Fetch payroll data
      let query = supabase?.from('payroll_runs')?.select('*, payroll_items(*)')?.order('pay_period_end', { ascending: false })?.limit(10);

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data: payrollRuns, error } = await query;
      if (error) throw error;

      // Fetch compliance violations
      const { data: violations, error: violationsError } = await supabase?.from('compliance_violations')?.select('*')?.eq('violation_status', 'identified')?.order('identified_at', { ascending: false });

      if (violationsError) throw violationsError;

      // Analyze with OpenAI
      const prompt = `Analyze this payroll data for PNG Employment Act compliance violations before submission deadline:

Payroll Runs: ${payrollRuns?.length || 0}
Existing Violations: ${violations?.length || 0}

Payroll Summary:
${JSON.stringify(payrollRuns?.slice(0, 3), null, 2)}

Existing Violations:
${JSON.stringify(violations?.slice(0, 5), null, 2)}

Identify:
1. Critical violations that must be fixed before submission
2. Deadline-sensitive issues
3. Patterns indicating systemic problems
4. Recommended actions with priority levels`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5',
        messages: [
          { role: 'system', content: 'You are a PNG payroll compliance expert. Focus on deadline-critical violations.' },
          { role: 'user', content: prompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'scan_results',
            schema: {
              type: 'object',
              properties: {
                criticalViolations: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      type: { type: 'string' },
                      description: { type: 'string' },
                      severity: { type: 'string' },
                      affectedCount: { type: 'number' },
                      deadline: { type: 'string' },
                      actionRequired: { type: 'string' }
                    },
                    required: ['type', 'description', 'severity']
                  }
                },
                deadlineSensitiveIssues: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      issue: { type: 'string' },
                      deadline: { type: 'string' },
                      priority: { type: 'string' }
                    },
                    required: ['issue', 'priority']
                  }
                },
                systemicProblems: { type: 'array', items: { type: 'string' } },
                recommendations: { type: 'array', items: { type: 'string' } },
                overallRiskLevel: { type: 'string' }
              },
              required: ['criticalViolations', 'deadlineSensitiveIssues', 'recommendations', 'overallRiskLevel'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',
        verbosity: 'high'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error scanning payroll:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Get predictive compliance forecast using AI
   */
  async getPredictiveCompliance(historicalData) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5',
        messages: [
          { 
            role: 'system', 
            content: 'You are a predictive analytics expert for PNG payroll compliance. Analyze patterns and forecast potential violations.' 
          },
          { 
            role: 'user', 
            content: `Based on this historical compliance data, predict potential violations in the next 30 days:\n\n${JSON.stringify(historicalData, null, 2)}\n\nProvide forecasts with confidence levels and recommended preventive actions.` 
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'predictive_analysis',
            schema: {
              type: 'object',
              properties: {
                predictions: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      violationType: { type: 'string' },
                      likelihood: { type: 'number' },
                      timeframe: { type: 'string' },
                      potentialImpact: { type: 'string' },
                      preventiveActions: { type: 'array', items: { type: 'string' } }
                    },
                    required: ['violationType', 'likelihood', 'timeframe']
                  }
                },
                overallRiskScore: { type: 'number' },
                keyRiskFactors: { type: 'array', items: { type: 'string' } },
                recommendations: { type: 'array', items: { type: 'string' } }
              },
              required: ['predictions', 'overallRiskScore', 'recommendations'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',
        verbosity: 'medium'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error getting predictive compliance:', error);
      }
      throw new Error(errorInfo.message);
    }
  }
};

export default openaiComplianceService;